import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';

export function getEffectiveRestaurantId() {
  const stored = localStorage.getItem('restaurantId');
  if (stored) return stored;
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.restaurantId != null) return String(user.restaurantId);
  } catch {
    // ignore
  }
  return '';
}

export function getCurrentCompanyUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const id = Number(user?.userId ?? user?.id ?? user?.User_Id ?? 0);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function buildDateParams({ fromDate, toDate } = {}) {
  const params = {};
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  return params;
}

function money(value) {
  if (value == null || value === '') return '0.00';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatSettlementAmount(value) {
  return `₹${money(value)}`;
}

/** GET combined unsettled payable (subscription + regular). */
export async function fetchSettlementsPayableSummary(restaurantId, { fromDate, toDate } = {}) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantSettlementsPayableSummary(restaurantId),
    { params: buildDateParams({ fromDate, toDate }) }
  );
  return response.data;
}

/** GET subscription credit unsettled lines/orders. */
export async function fetchSubscriptionUnsettled(restaurantId, { fromDate, toDate } = {}) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantSubscriptionSettlementsUnsettled(restaurantId),
    { params: buildDateParams({ fromDate, toDate }) }
  );
  return response.data;
}

/** GET regular order unsettled payments. */
export async function fetchOrderUnsettled(restaurantId, { fromDate, toDate } = {}) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantOrderSettlementsUnsettled(restaurantId),
    { params: buildDateParams({ fromDate, toDate }) }
  );
  return response.data;
}

/** GET subscription settlement batches. */
export async function fetchSubscriptionSettlementBatches(restaurantId, { status } = {}) {
  const params = {};
  if (status) params.status = status;
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantSubscriptionSettlementBatches(restaurantId),
    { params }
  );
  return response.data;
}

/** GET regular order settlement batches. */
export async function fetchOrderSettlementBatches(restaurantId, { status } = {}) {
  const params = {};
  if (status) params.status = status;
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantOrderSettlementBatches(restaurantId),
    { params }
  );
  return response.data;
}

/** GET batch detail with lines. */
export async function fetchSettlementBatchDetail(batchId, settlementType) {
  const path =
    settlementType === 'regular'
      ? RESTAURANT_API_PATHS.restaurantOrderSettlementBatchById(batchId)
      : RESTAURANT_API_PATHS.restaurantSubscriptionSettlementBatchById(batchId);
  const response = await axiosRestaurantInstance.get(path);
  return response.data;
}

/**
 * POST create settlement batch.
 * @param {'subscription'|'regular'} settlementType
 */
export async function createSettlementBatch(restaurantId, settlementType, body) {
  const path =
    settlementType === 'regular'
      ? RESTAURANT_API_PATHS.restaurantOrderSettlementBatches(restaurantId)
      : RESTAURANT_API_PATHS.restaurantSubscriptionSettlementBatches(restaurantId);
  const response = await axiosRestaurantInstance.post(path, body);
  return response.data;
}

/**
 * PUT mark batch paid.
 * @param {'subscription'|'regular'} settlementType
 */
export async function markSettlementBatchPaid(batchId, settlementType, body) {
  const path =
    settlementType === 'regular'
      ? RESTAURANT_API_PATHS.restaurantOrderSettlementBatchMarkPaid(batchId)
      : RESTAURANT_API_PATHS.restaurantSubscriptionSettlementBatchMarkPaid(batchId);
  const response = await axiosRestaurantInstance.put(path, body);
  return response.data;
}

/**
 * PUT cancel pending batch.
 * @param {'subscription'|'regular'} settlementType
 */
export async function cancelSettlementBatch(batchId, settlementType, body) {
  const path =
    settlementType === 'regular'
      ? RESTAURANT_API_PATHS.restaurantOrderSettlementBatchCancel(batchId)
      : RESTAURANT_API_PATHS.restaurantSubscriptionSettlementBatchCancel(batchId);
  const response = await axiosRestaurantInstance.put(path, body);
  return response.data;
}
